import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay
} from '@chakra-ui/react';
import React, { useContext, useEffect, useMemo, useState, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Flex,
  Box,
  Input,
  Textarea,
  ModalFooter,
  Button,
  useToast,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Text,
  Tooltip,
  Badge,
  Grid
} from '@chakra-ui/react';
import { WebAPIContext } from '../../contexts/WebAPIContext';
import { ApproveResult, WorkflowId, InputLimits } from '../../contexts/constants';
import { useAppSelector } from '../../store/hooks';
import { useNavigate } from "react-router-dom";
import { ProjectContext } from './InvestmentScreeningDetail';
import { ProjectContextType } from '../../contexts/types';
import { ApproverList } from '../../contexts/types';
import { useTranslation } from 'react-i18next';
import i18n from '../../utils/i18n';
import dayjs from 'dayjs';
import ApprovalListComponent from './ApprovalListComponent';

/** flow_id → 承認区分名マッピング */
const getFlowIdToStageName = (): Record<string, string> => ({
  [WorkflowId.ProductDirector]: i18n.t('common.constants.workflowStage.productDirector'),
  [WorkflowId.BusinessManager]: i18n.t('common.constants.workflowStage.businessManager'),
  [WorkflowId.CrossCheck]: i18n.t('common.constants.workflowStage.crossCheck'),
  [WorkflowId.MaterialOfficerRequester]: i18n.t('common.constants.workflowStage.materialOfficerRequester'),
  [WorkflowId.MaterialOfficer]: i18n.t('common.constants.workflowStage.materialOfficer'),
  [WorkflowId.SalesHQManagerRequester]: i18n.t('common.constants.workflowStage.salesHQManagerRequester'),
  [WorkflowId.SalesHQManager]: i18n.t('common.constants.workflowStage.salesHQManager'),
  [WorkflowId.BusinessHQManagerRequester]: i18n.t('common.constants.workflowStage.businessHQManagerRequester'),
  [WorkflowId.BusinessHQManager]: i18n.t('common.constants.workflowStage.businessHQManager'),
  [WorkflowId.PreReview]: i18n.t('common.constants.workflowStage.preReview'),
  [WorkflowId.HQReview]: i18n.t('common.constants.workflowStage.hqReview'),
  [WorkflowId.PresidentReport]: i18n.t('common.constants.workflowStage.presidentReport'),
});

// ============================================================
// 承認ステップ表示用の定義
// ============================================================

/** 並列承認ブロックの系統定義（確認依頼者 → 承認者） */
const PARALLEL_BLOCK_1: { laneFlowId: string; requester?: string; approver: string }[] = [
  { laneFlowId: WorkflowId.CrossCheck, approver: WorkflowId.CrossCheck },
  { laneFlowId: WorkflowId.MaterialOfficer, requester: WorkflowId.MaterialOfficerRequester, approver: WorkflowId.MaterialOfficer },
];

/** 並列承認ブロックの系統定義（確認依頼者 → 承認者） */
const PARALLEL_BLOCK_2: { laneFlowId: string; requester: string; approver: string }[] = [
  { laneFlowId: WorkflowId.SalesHQManager, requester: WorkflowId.SalesHQManagerRequester, approver: WorkflowId.SalesHQManager },
  { laneFlowId: WorkflowId.BusinessHQManager, requester: WorkflowId.BusinessHQManagerRequester, approver: WorkflowId.BusinessHQManager },
];

/** 並列ブロックより前の直列ステップ */
const SERIAL_BEFORE: string[] = [
  WorkflowId.ProductDirector,
  WorkflowId.BusinessManager,
];

/** 並列ブロックより後の直列ステップ */
const SERIAL_AFTER: string[] = [
  WorkflowId.PreReview,
  WorkflowId.HQReview,
  WorkflowId.PresidentReport,
];

/** ステップ状態 */
type StageStatus = 'done' | 'current' | 'remanded' | 'upcoming';

/** バックボーン（横フロー）の1ノード */
interface StageNode {
  /** 'stage': 単一ステップ / 'parallel': 並列承認ブロックのプレースホルダー */
  kind: 'stage' | 'parallel';
  flowId?: string;
  label: string;
  /** フロー全体での通し番号（1始まり）。parallelの場合は開始番号 */
  stageNo: number;
  /** parallelの場合の終了番号 */
  stageNoEnd?: number;
  status: StageStatus;
  /** 承認済みの場合の承認日（MM/DD） */
  doneDate?: string;
  /** 並列の場合の系統数 */
  laneCount?: number;
  // 並列ノードの内訳データ
  lanes?: {
    label: string;
    hasRequester: boolean;
    requester?: { flowId: string; status: StageStatus };
    approver: { flowId: string; status: StageStatus };
  }[];
}

/** 案件サマリー（親から渡す表示用データ） */
export interface DetailModalSummary {
  /** 案件名 */
  projectName?: string;
  /** 申請番号 */
  applicationNumber?: string;
  /** 申請者名 */
  applicantName?: string;
  /** 申請金額（親で通貨換算・フォーマット済みの表示文字列） */
  applicationAmount?: string;
  /** 申請日（フォーマット済みの表示文字列） */
  applicationDate?: string;
}

interface InvestmentScreeningDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  flowId: string;
  /** 承認者一覧（親のgetApproverList結果をそのまま渡す） */
  approverList?: ApproverList[];
  /** 案件サマリー表示用データ */
  summary?: DetailModalSummary;
}

// 型定義
interface updateApprovalResultResponse {
  data: {
    updateApprovalResult: {
      result: string;
    };
  };
}

interface MailResult {
  result: string;
  failedAddresses?: string[];
  sentCount?: number;
}

interface sendApprovalRequestMailResponse {
  data: {
    sendApprovalRequestMail: MailResult;
  };
}

interface sendApprovalMailResponse {
  data: {
    sendApprovalMail: MailResult;
  };
}

interface sendRemandMailResponse {
  data: {
    sendRemandMail: MailResult;
  };
}

interface sendRejectionMailResponse {
  data: {
    sendRejectionMail: MailResult;
  };
}

interface approveStatus {
  result: string;
}

/**
 * 投資申請詳細モーダル
 * @param isOpen 表示フラグ
 * @param onClose クローズ関数
 * @param projectId プロジェクトID
 * @param flowId フローID
 * @param approverList 承認者一覧（親で取得済みのものをprops渡し）
 * @param summary 案件サマリー表示用データ
 */
const InvestmentScreeningDetailModal: React.FC<InvestmentScreeningDetailModalProps> = ({ isOpen, onClose, projectId, flowId, approverList = [], summary }) => {
  const { t } = useTranslation();
  const m = "features.InvestmentScreening.DetailModal";
  const toast = useToast();
  const WebAPI = useContext(WebAPIContext);
  const employeeId = useAppSelector((state) => state.employee.employeeId); // ログインユーザーの従業員ID
  const divisionId = useAppSelector((state) => state.employee.divisionId);
  const { reviewDate } = useContext(ProjectContext) as ProjectContextType;
  const [processing, setProcessing] = useState<boolean>(false); // 処理中フラグ
  const [comment, setComment] = useState<string>(''); // コメントテキストボックス
  const [commentError, setCommentError] = useState<string>(''); // コメントエラーメッセージ
  const [chairName, setChairName] = useState<string>(''); // 議長名（本部長検討会の場合のみ）
  const [chairNameError, setChairNameError] = useState<string>(''); // 議長名エラーメッセージ

  // 確認ダイアログ
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [alertAction, setAlertAction] = useState<() => void>(() => { });
  const [alertMessage, setAlertMessage] = useState<string>('');

  const cancelRef = useRef(null);

  // 確認ダイアログClose
  const onCloseAlert = () => setIsAlertOpen(false);

  // 確認ダイアログ表示
  const showAlert = (message: string) => {
    setAlertMessage(message);
    setIsAlertOpen(true);
  };

  const navigate = useNavigate();

  // ============================================================
  // 承認ステップ表示の構築（approverListから動的生成）
  // ============================================================

  /**
   * flowId単位の状態判定
   * @param targetFlowId FloeId
   * @param  entries 担当者データ・IDと承認リザルト
   * @returns status
   * - done: 全担当者が承認済み
   * - remanded: 差戻あり
   * - current: このモーダルの対象flowId
   * - upcoming: 未着手
   */
  const getStageInfo = (targetFlowId: string, entries: ApproverList[]): { status: StageStatus; doneDate?: string } => {
    // 1. このモーダルで現在処理しようとしているステップなら「current（現在地）」
    if (targetFlowId === flowId) {
      return { status: 'current' };
    }
    // 2. 担当者のデータが無ければ「upcoming（未着手）」
    if (entries.length === 0) {
      return { status: 'upcoming' };
    }
    // 3. 誰か一人でも「差戻し (Remand)」していれば「remanded（差戻し）」
    if (entries.some((e) => e.approvalResult === ApproveResult.Remand.code)) {
      return { status: 'remanded' };
    }
    // 4. 承認完了判定（並列後の直列ステップかどうかでルールを切り替える）
    const isSingleApprovalMode = SERIAL_AFTER.includes(targetFlowId);

    // 並列処理or経営管理部承認ステージで判別条件切り替えて抽出する
    const isApproval = isSingleApprovalMode
      ? entries.some((e) => e.approvalResult === ApproveResult.Approval.code)
      : entries.every((e) => e.approvalResult === ApproveResult.Approval.code);

    if (isApproval) {
      // 経営管理部承認ステージの場合は、承認済みの人のデータだけから最新の完了日付を抽出する。
      const targetEnteries = isSingleApprovalMode
        ? entries.filter((e) => e.approvalResult === ApproveResult.Approval.code)
        : entries;

      // 最新の確認日時をMM/DD表示（ロケールに応じた表示形式）
      const latest = targetEnteries
        .map((e) => e.confirmTime)
        .filter((v): v is string => !!v)
        .sort()
        .pop();
      const monthDayPattern = i18n.language === 'en' ? 'MMM D' : 'MM/DD';
      return { status: 'done', doneDate: latest ? dayjs(latest).format(monthDayPattern) : undefined };
    }
    // 5. 上記以外（承認待ちの人がいるなど）は「upcoming」
    return { status: 'upcoming' };
  };

  /** 承認ステップ表示モデル（フロー構成は案件によって変動するためapproverListから動的生成）
   * @param stageNodes 承認ステージ情報
   * @param totalStage 全体の承認ステージ数
   * @param currentStageNo 現在の承認ステージの位置番号
   */
  const { stageNodes, totalStages, currentStageNo } = useMemo(() => {
    const stageNames = getFlowIdToStageName();
    // 一致するIDをフィルター
    const byFlowId = (id: string) => approverList.filter((a) => a.flowId === id);
    // IDが存在かつ現在のFlowIdと同一か判定：Boolean
    const hasFlow = (id: string) => byFlowId(id).length > 0 || id === flowId;

    // 案件に存在する並列系統のみ抽出・flowIdを展開
    const processBlock = (blockDef: typeof PARALLEL_BLOCK_1) => {
      // 案件の抽出
      const lanes = blockDef
        // 存在するレーンのみ抽出
        .filter((lane) => (lane.requester && hasFlow(lane.requester)) || hasFlow(lane.approver))
        .map((lane) => {
          // Requesterへ存在チェック
          const hasReq = !!lane.requester && hasFlow(lane.requester);
          return {
            label: stageNames[lane.approver] || lane.approver,
            hasRequester: hasReq,
            requester: hasReq && lane.requester ? { flowId: lane.requester, ...getStageInfo(lane.requester, byFlowId(lane.requester)) } : undefined,
            approver: { flowId: lane.approver, ...getStageInfo(lane.approver, byFlowId(lane.approver)) },
          };
        });
      // FlowOdを展開
      const flowIds = lanes.flatMap((lane) => {
        const arr: string[] = [];
        // 依頼者がいれば（CC以外なら）追加
        if (lane.requester?.flowId) {
          arr.push(lane.requester.flowId);
        }
        // 承認者は必ず追加
        arr.push(lane.approver.flowId);
        return arr;
      });

      return { lanes, flowIds };
    }


    // 通し番号の計算用: 実在するflowIdを順序どおりに展開
    const serialBefore = SERIAL_BEFORE.filter(hasFlow);
    const block1 = processBlock(PARALLEL_BLOCK_1);
    const block2 = processBlock(PARALLEL_BLOCK_2);
    const serialAfter = SERIAL_AFTER.filter(hasFlow);
    // 1つの配列に結合
    const orderedFlowIds = [...serialBefore, ...block1.flowIds, ...block2.flowIds, ...serialAfter];
    // 配列内のインデックス + 1 が、そのステップの通し番号になる
    const stageNoOf = (id: string) => orderedFlowIds.indexOf(id) + 1;

    const nodes: StageNode[] = [];
    // 直列ノード（並列前）にステータスを追加　承認済み等
    for (const id of serialBefore) {
      const info = getStageInfo(id, byFlowId(id));
      nodes.push({
        kind: 'stage',
        flowId: id,
        label: stageNames[id] || id,
        stageNo: stageNoOf(id),
        status: info.status,
        doneDate: info.doneDate,
      });
    }

    // 並列ブロックを評価して nodes に追加するヘルパー関数
    const addParallelNode = (block: { lanes: any[], flowIds: string[] }, label: string) => {
      if (block.lanes.length === 0) return; // レーンが無ければ何もしない

      // ステータスの集計と判定
      const statuses = block.lanes.flatMap((l) => [l.requester?.status, l.approver.status]).filter(Boolean) as StageStatus[];
      // 優先順位: current があれば current > remanded があれば remanded > 全て done なら done
      let blockStatus: StageStatus = 'upcoming';
      if (statuses.includes('current')) blockStatus = 'current';
      else if (statuses.includes('remanded')) blockStatus = 'remanded';
      else if (statuses.every((s) => s === 'done')) blockStatus = 'done';

      // nodes配列に追加
      nodes.push({
        kind: 'parallel',
        label: label,
        stageNo: stageNoOf(block.flowIds[0]),
        stageNoEnd: stageNoOf(block.flowIds[block.flowIds.length - 1]),
        status: blockStatus,
        laneCount: block.lanes.length,
        lanes: block.lanes,
      });
    };

    // 並列ノードをステータスに追加
    addParallelNode(block1, t(`${m}.label.parallelApproval1`));
    addParallelNode(block2, t(`${m}.label.parallelApproval2`));

    // 直列ノード（並列後）にステータスを追加
    for (const id of serialAfter) {
      const info = getStageInfo(id, byFlowId(id));
      nodes.push({
        kind: 'stage',
        flowId: id,
        label: stageNames[id] || id,
        stageNo: stageNoOf(id),
        status: info.status,
        doneDate: info.doneDate,
      });
    }

    return {
      stageNodes: nodes,
      totalStages: orderedFlowIds.length,
      currentStageNo: stageNoOf(flowId),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approverList, flowId, t]);

  // ワークフローID単位の承認状況取得API
  const getApproveStatusByFlowId = async () => {

    let approveStatus: approveStatus = { result: 'false' };

    try {
      console.log(`getProjectData():start`);
      const payload = {
        query: `
          query getApproveStatusByFlowId($projectId: String, $flowId: String, $reviewDate: String) {
            getApproveStatusByFlowId(projectId: $projectId, flowId: $flowId, reviewDate: $reviewDate) {
              result
            }
          }`,
        variables: {
          projectId: projectId,
          flowId: flowId,
          ...(reviewDate && { reviewDate }),
        }
      };

      // プロジェクト情報取得（dynamodb、RDS）
      const response = await WebAPI.request(payload);

      // プロジェクト情報
      if (response && response.data && response.data.getApproveStatusByFlowId) {
        approveStatus = response.data.getApproveStatusByFlowId;
      }

      return approveStatus;
    } catch (error) {
      console.error("データ取得エラー:", error);
      return approveStatus;
    }
  };

  // 承認結果更新API
  const updateApprovalResult = async (approvalResult: string): Promise<boolean> => {
    const functionName = 'updateApprovalResult';
    try {
      console.log(`${functionName} START`);
      console.info(`approvalResult: ${approvalResult}`);
      const result: updateApprovalResultResponse = await WebAPI.request({
        query: `
          mutation ${functionName}(
            $projectId: String,
            $flowId: String,
            $employeeId: String,
            $comment: String,
            $approvalResult: String,
            $chairName: String,
            $reviewDate: String
          ) {
            ${functionName}(
              projectId: $projectId,
              flowId: $flowId,
              employeeId: $employeeId,
              comment: $comment,
              approvalResult: $approvalResult,
              chairName: $chairName,
              reviewDate: $reviewDate
            ) {
              result
            }
          }
        `,
        variables: {
          projectId, flowId, employeeId, comment, approvalResult,
          chairName: flowId === WorkflowId.HQReview ? chairName : undefined,
          ...(reviewDate && { reviewDate })
        }
      });
      console.log(`${functionName} RESULT:`, result);
      return result?.data?.updateApprovalResult?.result === 'true';
    } catch (error) {
      console.error(`${functionName} ERROR:`, error);
      return false;
    } finally {
      console.log(`${functionName} END`);
    }
  };

  // 承認依頼メール送信API
  const sendApprovalRequestMail = async (projectId: string): Promise<{ success: boolean; failedAddresses?: string[]; sentCount?: number }> => {
    const functionName = 'sendApprovalRequestMail';
    try {
      console.log(`${functionName} START`);
      console.info(`projectId: ${projectId}`);
      // 次の承認ステージのflow_idマッピング
      const nextFlowIdMap: Record<string, string> = {
        [WorkflowId.ProductDirector]: WorkflowId.BusinessManager,     // 製品統括 → 事業部長
        [WorkflowId.BusinessManager]: WorkflowId.SalesHQManager,      // 事業部長 → 営業本部長（並列の最初）
        // 並列承認以降のメール送信はGATE遷移時に別途検討（Q10）
      };

      // 並列承認のflow_idには「次の承認者」がないのでメール送信をスキップ
      if (!nextFlowIdMap[flowId]) {
        console.log(`${functionName} SKIP: no next flowId for flowId=${flowId}`);
        return { success: true };
      }
      const nextFlowId = nextFlowIdMap[flowId];

      const result: sendApprovalRequestMailResponse = await WebAPI.request({
        query: `
          mutation ${functionName}(
            $projectId: String,
            $flowId: String,
            $reviewDate: String,
          ) {
            ${functionName}(
              projectId: $projectId,
              flowId: $flowId,
              reviewDate: $reviewDate,
            ) {
              result
              failedAddresses
              sentCount
            }
          }
        `,
        variables: { projectId, flowId: nextFlowId, ...(reviewDate && { reviewDate }) }
      });
      console.log(`${functionName} RESULT:`, result);
      const mailResult = result?.data?.sendApprovalRequestMail;
      return { success: mailResult?.result === 'true', failedAddresses: mailResult?.failedAddresses, sentCount: mailResult?.sentCount };
    } catch (error) {
      console.error(`${functionName} ERROR:`, error);
      return { success: false };
    } finally {
      console.log(`${functionName} END`);
    }
  };

  // 承認完了登録API
  const putApproveComplete = async (): Promise<string> => {
    const functionName = 'putApproveComplete';
    try {
      console.log(`${functionName} START`);

      const input = {
        projectId: projectId,
        employeeId: employeeId,
        divisionId: divisionId,
        ...(reviewDate && { reviewDate }),
      };
      const query = `
        mutation ${functionName} ($input: ApproveCompleteInput!) {
          ${functionName}(input: $input) {status}
        }
      `;
      const result = await WebAPI.request({
        query: query,
        variables: { input }
      });
      console.log(`${functionName} RESULT:`, result);
      return result?.data?.putApproveComplete?.status;
    } catch (error) {
      console.error(`${functionName} ERROR:`, error);
      return `error`;
    } finally {
      console.log(`${functionName} END`);
    }
  };

  // 承認完了メール送信API
  const sendApprovalMail = async (projectId: string): Promise<{ success: boolean; failedAddresses?: string[]; sentCount?: number }> => {
    const functionName = 'sendApprovalMail';
    try {
      console.log(`${functionName} START`);
      console.info(`projectId: ${projectId}`);
      const result: sendApprovalMailResponse = await WebAPI.request({
        query: `
          mutation ${functionName}(
            $projectId: String,
            $reviewDate: String
          ) {
            ${functionName}(
              projectId: $projectId,
              reviewDate: $reviewDate
            ) {
              result
              failedAddresses
              sentCount
            }
          }
        `,
        variables: { projectId, ...(reviewDate && { reviewDate }) }
      });
      console.log(`${functionName} RESULT:`, result);
      const mailResult = result?.data?.sendApprovalMail;
      return { success: mailResult?.result === 'true', failedAddresses: mailResult?.failedAddresses, sentCount: mailResult?.sentCount };
    } catch (error) {
      console.error(`${functionName} ERROR:`, error);
      return { success: false };
    } finally {
      console.log(`${functionName} END`);
    }
  };

  // 差戻しメール送信API
  const sendRemandMail = async (projectId: string): Promise<{ success: boolean; failedAddresses?: string[]; sentCount?: number }> => {
    const functionName = 'sendRemandMail';
    try {
      console.log(`${functionName} START`);
      console.info(`projectId: ${projectId}`);
      const result: sendRemandMailResponse = await WebAPI.request({
        query: `
          mutation ${functionName}(
            $projectId: String,
            $reviewDate: String
          ) {
            ${functionName}(
              projectId: $projectId,
              reviewDate: $reviewDate
            ) {
              result
              failedAddresses
              sentCount
            }
          }
        `,
        variables: { projectId, ...(reviewDate && { reviewDate }) }
      });
      console.log(`${functionName} RESULT:`, result);
      const mailResult = result?.data?.sendRemandMail;
      return { success: mailResult?.result === 'true', failedAddresses: mailResult?.failedAddresses, sentCount: mailResult?.sentCount };
    } catch (error) {
      console.error(`${functionName} ERROR:`, error);
      return { success: false };
    } finally {
      console.log(`${functionName} END`);
    }
  };

  // 却下メール送信API
  const sendRejectionMail = async (projectId: string): Promise<{ success: boolean; failedAddresses?: string[]; sentCount?: number }> => {
    const functionName = 'sendRejectionMail';
    try {
      console.log(`${functionName} START`);
      console.info(`projectId: ${projectId}`);
      const result: sendRejectionMailResponse = await WebAPI.request({
        query: `
          mutation ${functionName}(
            $projectId: String,
            $reviewDate: String
          ) {
            ${functionName}(
              projectId: $projectId,
              reviewDate: $reviewDate
            ) {
              result
              failedAddresses
              sentCount
            }
          }
        `,
        variables: { projectId, ...(reviewDate && { reviewDate }) }
      });
      console.log(`${functionName} RESULT:`, result);
      const mailResult = result?.data?.sendRejectionMail;
      return { success: mailResult?.result === 'true', failedAddresses: mailResult?.failedAddresses, sentCount: mailResult?.sentCount };
    } catch (error) {
      console.error(`${functionName} ERROR:`, error);
      return { success: false };
    } finally {
      console.log(`${functionName} END`);
    }
  };

  // 承認ボタン押下処理
  const handleApproveRequest = async () => {

    // 本部長検討会の場合: 議長名の必須チェック（確認ダイアログ表示前に検証）
    setChairNameError('');
    if (flowId === WorkflowId.HQReview && !chairName.trim()) {
      setChairNameError(t(`${m}.validation.chairNameRequired`));
      return;
    }

    const action = async () => {

      const functionName = 'handleApproveRequest';
      try {
        console.log(`${functionName} START`);
        if (processing) {
          // 処理中の場合
          return;
        }
        setProcessing(true);
        // 承認結果更新APIを実行
        let result = false
        result = await updateApprovalResult(ApproveResult.Approval.code);
        if (!result) {
          // 承認結果更新APIに失敗した場合
          toast({
            title: t(`${m}.message.updateResultFailedTitle`),
            description: t(`${m}.message.updateResultFailed`),
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          return;
        }

        // 承認成功（メール送信はバックエンド側で処理）
        toast({
          title: t(`${m}.message.approvalRequestTitle`),
          description: t(`${m}.message.approvalDone`),
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        // ホーム画面に遷移
        navigate("/");
      } catch (error) {
        console.error(`${functionName} ERROR:`, error);
      } finally {
        console.log(`${functionName} END`);
        setProcessing(false);
        onClose();
      }
    };

    setAlertAction(() => action);
    showAlert(t(`${m}.dialog.confirmApprove`));
  };

  // 承認ボタン押下処理
  const handleApprove = async () => {

    const action = async () => {

      const functionName = 'handleApprove';
      try {
        console.log(`${functionName} START`);
        if (processing) {
          // 処理中の場合
          return;
        }
        setProcessing(true);
        // 承認結果更新APIを実行
        let result = false;
        result = await updateApprovalResult(ApproveResult.Approval.code);
        if (!result) {
          // 承認結果更新APIに失敗した場合
          toast({
            title: t(`${m}.message.updateResultFailedTitle`),
            description: t(`${m}.message.updateResultFailed`),
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          return;
        }

        const approveStatus = await getApproveStatusByFlowId();
        // 未承認が居る場合は承認完了登録、メール送信を行わない
        if (approveStatus.result === "false") {
          toast({
            title: t(`${m}.message.approvalRequestTitle`),
            description: t(`${m}.message.approvalDone`),
            status: 'success',
            duration: 5000,
            isClosable: true,
          });
          navigate("/");
          return;
        }

        // ワークフロー更新(承認完了)
        const status = await putApproveComplete();
        if (status !== `success`) {
          // 失敗
          toast({
            title: t(`${m}.message.approvalCompleteFailedTitle`),
            description: t(`${m}.message.approvalCompleteFailed`),
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          return;
        }

        // 承認完了メール送信APIを実行
        const mailResult = await sendApprovalMail(projectId);
        if (!mailResult.success) {
          const failedDesc = mailResult.failedAddresses?.length
            ? t(`${m}.message.sendFailedAddresses`, { addresses: mailResult.failedAddresses.join(', ') })
            : t(`${m}.message.approvalMailFailed`);
          toast({
            title: t(`${m}.message.approvalMailFailedTitle`),
            description: failedDesc,
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        } else {
          toast({
            title: t(`${m}.message.approvalMailSentTitle`),
            description: t(`${m}.message.mailSent`),
            status: 'success',
            duration: 5000,
            isClosable: true,
          });
        }
        // ホーム画面に遷移
        navigate("/");
      } catch (error) {
        console.error(`${functionName} ERROR:`, error);
      } finally {
        console.log(`${functionName} END`);
        setProcessing(false);
        onClose();
      }
    };

    setAlertAction(() => action);
    showAlert(t(`${m}.dialog.confirmApprove`));
  };

  // 差戻ボタン押下処理
  const handleRemand = async () => {

    const action = async () => {
      const functionName = 'handleRemand';
      let errorFlg = false;
      try {
        console.log(`${functionName} START`);
        if (processing) {
          // 処理中の場合
          return;
        }
        // コメントが未入力
        if (!comment.trim()) { errorFlg = true; }

        if (errorFlg) {
          setCommentError(t(`${m}.validation.commentRequired`));
          return;
        }
        setProcessing(true);
        // 承認結果更新APIを実行
        let result = false
        result = await updateApprovalResult(ApproveResult.Remand.code);
        if (!result) {
          // 承認結果更新APIに失敗した場合
          toast({
            title: t(`${m}.message.updateResultFailedTitle`),
            description: t(`${m}.message.updateResultFailed`),
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          return;
        }
        // 差戻しメール送信APIを実行
        const remandMailResult = await sendRemandMail(projectId);
        if (!remandMailResult.success) {
          const failedDesc = remandMailResult.failedAddresses?.length
            ? t(`${m}.message.sendFailedAddresses`, { addresses: remandMailResult.failedAddresses.join(', ') })
            : t(`${m}.message.remandMailFailed`);
          toast({
            title: t(`${m}.message.remandMailFailedTitle`),
            description: failedDesc,
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        } else {
          toast({
            title: t(`${m}.message.remandMailSentTitle`),
            description: t(`${m}.message.mailSent`),
            status: 'success',
            duration: 5000,
            isClosable: true,
          });
        }
        // ホーム画面に遷移
        navigate("/");
      } catch (error) {
        console.error(`${functionName} ERROR:`, error);
      } finally {
        console.log(`${functionName} END`);
        setProcessing(false);
        if (!errorFlg) { onClose(); }
      }
    };

    setAlertAction(() => action);
    showAlert(t(`${m}.dialog.confirmRemand`));
  };

  // 却下ボタン押下処理
  const handleReject = async () => {
    const functionName = 'handleReject';
    try {
      console.log(`${functionName} START`);
      if (processing) {
        // 処理中の場合
        return;
      }
      setProcessing(true);
      // 承認結果更新APIを実行
      let result = false
      result = await updateApprovalResult(ApproveResult.Reject.code);
      if (!result) {
        // 承認結果更新APIに失敗した場合
        toast({
          title: t(`${m}.message.updateResultFailedTitle`),
          description: t(`${m}.message.updateResultFailed`),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      // 却下メール送信APIを実行
      const rejectionMailResult = await sendRejectionMail(projectId);
      if (!rejectionMailResult.success) {
        const failedDesc = rejectionMailResult.failedAddresses?.length
          ? t(`${m}.message.sendFailedAddresses`, { addresses: rejectionMailResult.failedAddresses.join(', ') })
          : t(`${m}.message.rejectionMailFailed`);
        toast({
          title: t(`${m}.message.rejectionMailFailedTitle`),
          description: failedDesc,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: t(`${m}.message.rejectionMailSentTitle`),
          description: t(`${m}.message.mailSent`),
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      }
      // ホーム画面に遷移
      navigate("/");
    } catch (error) {
      console.error(`${functionName} ERROR:`, error);
    } finally {
      console.log(`${functionName} END`);
      setProcessing(false);
      onClose();
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setComment(''); // コメントテキストボックスをクリア
      setCommentError(''); // エラーメッセージをクリア
      setChairName(''); // 議長名をクリア
      setChairNameError(''); // 議長名エラーをクリア
    }
  }, [isOpen]);

  // ============================================================
  // 承認ステップ描画パーツ
  // ============================================================

  /** ステップ円のスタイル */
  const stageCircle = (node: StageNode) => {
    const base = {
      w: '26px', h: '26px', borderRadius: 'full',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '12px', fontWeight: 'bold', flexShrink: 0,
    } as const;
    switch (node.status) {
      case 'done':
        return <Box {...base} bg="green.500" color="white">✓</Box>;
      case 'current':
        return <Box {...base} bg="blue.600" color="white" boxShadow="0 0 0 4px var(--chakra-colors-blue-50)">{node.stageNo}</Box>;
      case 'remanded':
        return <Box {...base} bg="red.500" color="white">!</Box>;
      default:
        return <Box {...base} bg="gray.100" color="gray.400" fontWeight="normal">{node.stageNo}</Box>;
    }
  };

  /** 系統内チップ（確認依頼者/承認者） */
  const laneChip = (label: string, status: StageStatus) => {
    const colorMap: Record<StageStatus, { bg: string; color: string; dot: string }> = {
      done: { bg: 'green.50', color: 'green.700', dot: 'green.500' },
      current: { bg: 'blue.50', color: 'blue.700', dot: 'blue.600' },
      remanded: { bg: 'red.50', color: 'red.700', dot: 'red.500' },
      upcoming: { bg: 'gray.100', color: 'gray.500', dot: 'gray.400' },
    };
    const c = colorMap[status];
    return (
      <Flex h="22px" px={2} borderRadius="6px" bg={c.bg} color={c.color} fontSize="10.5px" alignItems="center" gap={1}>
        <Box w="7px" h="7px" borderRadius="full" bg={c.dot} />
        {label}
      </Flex>
    );
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false} isCentered scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent maxWidth={900} minWidth={900} maxHeight="90vh" borderRadius="14px" overflow="hidden">
          {/* アクセントバー */}
          <Box h="4px" bgGradient="linear(to-r, blue.600, blue.400)" />
          <ModalHeader pb={2} fontSize="md" borderBottom="1px solid" borderColor="gray.100">
            {/* ステータスバッジ: 全n段階中 m番目 */}
            {totalStages > 0 && currentStageNo > 0 && (
              <Badge bg="blue.50" color="blue.600" borderRadius="6px" px={2.5} py={0.5} fontSize="12px" mb={1.5}>
                {t(`${m}.label.stagePosition`, { total: totalStages, current: currentStageNo })}
                {' ・ '}
                {t(`${m}.label.waitingApproval`)}
              </Badge>
            )}
            {flowId && getFlowIdToStageName()[flowId] && (
              <Text fontSize="lg" fontWeight="bold" color="black" mb={1}>{getFlowIdToStageName()[flowId]}</Text>
            )}
            <Text fontSize="sm" fontWeight="normal" color="gray.600">{t(`${m}.label.selectApprovalRemand`)}</Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex direction="column" height="100%">

              {/* ===== 承認ステップ ===== */}
              {stageNodes.length > 0 && (
                <Box mb={4}>
                  <Text fontSize="14px" fontWeight="bold" letterSpacing="0.08em" color="gray" mb={3}>
                    {t(`${m}.label.approvalSteps`)}
                  </Text>

                  {/* バックボーン（横フロー） */}
                  <Flex alignItems="flex-start" mb={stageNodes.length > 0 ? 3 : 0}>
                    {stageNodes.map((node, i) => (
                      <React.Fragment key={node.kind === 'stage' ? `stage-${node.flowId}` : `parallel-${node.label}`}>
                        {i > 0 && (
                          <Box
                            flex="1"
                            h="2px"
                            mt="13px"
                            mx={1}
                            bg={node.status === 'done' || node.status === 'current' ? 'blue.600' : 'gray.200'}
                          />
                        )}
                        {node.kind === 'stage' ? (
                          <Flex direction="column" alignItems="center" w="66px" flexShrink={0}>
                            {stageCircle(node)}
                            <Text
                              fontSize="10.5px"
                              mt={1.5}
                              textAlign="center"
                              lineHeight="1.35"
                              color={
                                node.status === 'current' ? 'blue.600' :
                                  node.status === 'done' ? 'gray.700' : 'gray.400'
                              }
                              fontWeight={node.status === 'current' ? 'bold' : 'normal'}
                            >
                              {node.label}
                            </Text>
                            {node.status === 'done' && node.doneDate && (
                              <Text fontSize="9.5px" color="gray.400">{node.doneDate}</Text>
                            )}
                            {node.status === 'current' && (
                              <Text fontSize="9.5px" color="blue.600">{t(`${m}.label.waitingApproval`)}</Text>
                            )}
                          </Flex>
                        ) : (
                          // 並列承認ブロック
                          <Flex direction="column" alignItems="center" w="118px" flexShrink={0}>
                            <Flex
                              h="26px"
                              px={3}
                              borderRadius="8px"
                              border="1.5px dashed"
                              borderColor={
                                node.status === 'done' ? 'green.300' :
                                  node.status === 'current' ? 'blue.300' :
                                    node.status === 'remanded' ? 'red.300' : 'gray.300'
                              }
                              bg={
                                node.status === 'done' ? 'green.50' :
                                  node.status === 'current' ? 'blue.50' :
                                    node.status === 'remanded' ? 'red.50' : 'gray.50'
                              }
                              color={
                                node.status === 'done' ? 'green.600' :
                                  node.status === 'current' ? 'blue.600' :
                                    node.status === 'remanded' ? 'red.600' : 'gray.500'
                              }
                              fontSize="11px"
                              fontWeight="bold"
                              alignItems="center"
                              gap={1.5}
                              whiteSpace="nowrap"
                            >
                              {node.label}
                              <Badge
                                bg={node.status === 'done' ? 'green.200' : node.status === 'remanded' ? 'red.200' : 'gray.300'}
                                color={node.status === 'done' ? 'green.700' : node.status === 'remanded' ? 'red.700' : 'gray.600'}
                                borderRadius="5px"
                                px={1.5}
                                fontSize="10px"
                              >
                                {t(`${m}.label.laneCount`, { count: node.laneCount })}
                              </Badge>
                            </Flex>
                            <Text fontSize="9.5px" color="gray.400" mt={1.5} textAlign="center">
                              {t(`${m}.label.parallelRange`, { from: node.stageNo, to: node.stageNoEnd })}
                            </Text>
                          </Flex>
                        )}
                      </React.Fragment>
                    ))}
                  </Flex>

                  {/* 並列承認ブロックの内訳 */}
                  {stageNodes
                    .filter((node) => node.kind === 'parallel' && (node.status === 'current' || node.status === 'remanded'))
                    .map((block, index) => (
                      <Box key={`parallel-breakdown-${index}`} border="1.5px dashed" borderColor="gray.200" borderRadius="10px" px={4} py={3} bg="gray.50">
                        <Text fontSize="11px" color="gray.500" mb={2.5}>
                          <Text as="b" color="gray.600">{t(`${m}.label.parallelBreakdownTitle`)}</Text>
                          {' ― '}
                          {t(`${m}.label.parallelBreakdownDesc`, { count: block.laneCount })}
                        </Text>
                        <Flex direction="column" gap={2}>
                          {block.lanes?.map((lane) => (
                            <Flex key={lane.label} alignItems="center" gap={3}>
                              <Text w="110px" fontSize="11px" color="gray.600" fontWeight="medium" flexShrink={0}>
                                {lane.label}
                              </Text>
                              <Flex alignItems="center" gap={1.5}>
                                {lane.requester && (
                                  <>
                                    {laneChip(t(`${m}.label.requester`), lane.requester.status)}
                                    <Text color="gray.300" fontSize="12px">→</Text>
                                  </>
                                )}
                                {laneChip(t(`${m}.label.approver`), lane.approver.status)}
                              </Flex>
                            </Flex>
                          ))}
                        </Flex>
                      </Box>
                    ))}
                </Box>
              )}

              {/* ===== 案件サマリー ===== */}
              {summary && (
                <Box bg="gray.50" border="1px solid" borderColor="gray.100" borderRadius="10px" px={4} py={3.5} mb={4}>
                  {summary.projectName && (
                    <Text fontSize="15px" fontWeight="bold" color="gray.800" mb={2.5}>{summary.projectName}</Text>
                  )}
                  <Grid templateColumns="1fr 1fr" gap="10px 24px">
                    {summary.applicationNumber && (
                      <Box minW={0}>
                        <Text fontSize="11px" color="gray.400" mb={0.5}>{t(`${m}.label.applicationNumber`)}</Text>
                        <Text fontSize="13px" color="gray.700">{summary.applicationNumber}</Text>
                      </Box>
                    )}
                    {summary.applicationAmount && (
                      <Box minW={0}>
                        <Text fontSize="11px" color="gray.400" mb={0.5}>{t(`${m}.label.applicationAmount`)}</Text>
                        <Text fontSize="13px" color="gray.700" fontWeight="bold">{summary.applicationAmount}</Text>
                      </Box>
                    )}
                    {summary.applicantName && (
                      <Box minW={0}>
                        <Text fontSize="11px" color="gray.400" mb={0.5}>{t(`${m}.label.applicant`)}</Text>
                        {/* 長い氏名（英語名など）は省略表示＋ホバーで全文ツールチップ */}
                        <Tooltip label={summary.applicantName} openDelay={300}>
                          <Text fontSize="13px" color="gray.700" isTruncated>{summary.applicantName}</Text>
                        </Tooltip>
                      </Box>
                    )}
                    {summary.applicationDate && (
                      <Box minW={0}>
                        <Text fontSize="11px" color="gray.400" mb={0.5}>{t(`${m}.label.applicationDate`)}</Text>
                        <Text fontSize="13px" color="gray.700">{summary.applicationDate}</Text>
                      </Box>
                    )}
                  </Grid>
                </Box>
              )}

              {/* ===== 議長名（本部長検討会のみ、コメントの上） ===== */}
              {flowId === WorkflowId.HQReview && (
                <FormControl mb={3} isInvalid={!!chairNameError}>
                  <Input
                    placeholder={t(`${m}.placeholder.chairName`)}
                    value={chairName}
                    maxLength={InputLimits.CHAIR_NAME}
                    onChange={(e) => { setChairName(e.target.value); setChairNameError(''); }}
                  />
                  <FormErrorMessage>{chairNameError}</FormErrorMessage>
                </FormControl>
              )}

              {/* ===== コメント ===== */}
              <FormControl isInvalid={!!commentError}>
                <FormLabel fontSize="13px" fontWeight="medium" color="gray.600" mb={1.5}>
                  {t(`${m}.label.comment`)}
                  <Text as="span" color="gray.400" fontWeight="normal" ml={1}>{t(`${m}.label.commentRequiredOnRemand`)}</Text>
                </FormLabel>
                <Textarea
                  placeholder={t(`${m}.placeholder.comment`)}
                  value={comment}
                  maxLength={InputLimits.APPROVAL_COMMENT}
                  onChange={(e) => setComment(e.target.value)}
                />
                <FormErrorMessage>{commentError}</FormErrorMessage>
              </FormControl>
            </Flex>
          </ModalBody>
          <ModalFooter bg="gray.50" borderTop="1px solid" borderColor="gray.100">
            {flowId === WorkflowId.PresidentReport ? (
              <Button colorScheme="blue" mr={3} onClick={handleApprove}>{t(`${m}.button.approve`)}</Button>
            ) : (
              <Button colorScheme="blue" mr={3} onClick={handleApproveRequest}>{t(`${m}.button.approve`)}</Button>
            )}
            <Button colorScheme="red" onClick={handleRemand}>{t(`${m}.button.remand`)}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog
        isOpen={isAlertOpen}
        leastDestructiveRef={cancelRef}
        onClose={onCloseAlert}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('common.button.confirm')}
            </AlertDialogHeader>

            <AlertDialogBody>
              {alertMessage}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button colorScheme="blue" onClick={() => { alertAction(); onCloseAlert(); }}>
                {t('common.button.ok')}
              </Button>
              <Button ref={cancelRef} onClick={onCloseAlert} ml={3}>
                {t('common.button.cancel')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default InvestmentScreeningDetailModal;
